import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMapPin, FiFacebook, FiTwitter, FiYoutube, FiInstagram, FiLinkedin } from 'react-icons/fi';
import axios from 'axios';

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.primaryDark};
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%),
    linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.02) 50%, transparent 100%);
  color: white;
  padding: 3rem 0 1rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      repeating-linear-gradient(
        45deg,
        transparent,
        transparent 20px,
        rgba(255, 255, 255, 0.01) 20px,
        rgba(255, 255, 255, 0.01) 40px
      );
    pointer-events: none;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: white;
    margin-bottom: 1rem;
    font-size: 1.25rem;
    font-weight: 800;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;

  svg {
    color: ${props => props.theme.colors.primaryLight};
    flex-shrink: 0;
  }
`;

const ContactLink = styled.a`
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.colors.primaryLight};
    text-decoration: underline;
  }
`;

const QuickLinks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: color 0.3s ease;

  &:hover {
    color: white;
    font-weight: 700;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }

  svg {
    font-size: 1.25rem;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const PoweredBy = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  
  a {
    color: ${props => props.theme.colors.primaryLight};
    text-decoration: none;
    font-weight: 700;
    transition: color 0.3s ease;
    
    &:hover {
      color: white;
    }
  }
`;

const ClickableCopyright = styled.p`
  cursor: pointer;
  transition: color 0.3s ease;
  user-select: none;
  
  &:hover {
    color: ${props => props.theme.colors.primaryLight};
    text-decoration: underline;
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [siteSettings, setSiteSettings] = useState({
    contactInfo: {
      email: 'admin@rplsupport.com.au',
      phone: '+61 433 098 399',
      phoneSecondary: '+61 415 820 320',
      businessHours: 'Mon - Sat: 8:00 am - 7:00 pm'
    },
    socialMedia: {
      facebook: 'javascript:void(0)',
      instagram: 'javascript:void(0)',
      youtube: 'javascript:void(0)',
      linkedin: 'javascript:void(0)'
    }
  });

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/public/site-settings');
        if (response.data) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    
    fetchSettings();
  }, []);

  const handleCopyrightClick = () => {
    navigate('/admin/login');
  };

  const quickLinks = [
    { path: '/courses', label: 'Courses' },
    { path: '/about', label: 'About Us' },
    { path: '/faq', label: 'FAQ' },
    { path: '/privacy-policy', label: 'Privacy Policy' },
    { path: '/terms-conditions', label: 'Terms & Conditions' },
    { path: '/blog', label: 'Blog' }
  ];

  const socialLinks = [
    { icon: FiFacebook, url: siteSettings.socialMedia.facebook, label: 'Facebook' },
    { icon: FiInstagram, url: siteSettings.socialMedia.instagram, label: 'Instagram' },
    { icon: FiYoutube, url: siteSettings.socialMedia.youtube, label: 'YouTube' },
    { icon: FiLinkedin, url: siteSettings.socialMedia.linkedin, label: 'LinkedIn' }
  ];

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <h3>Contact Info</h3>
            <ContactInfo>
              <ContactItem>
                <FiPhone />
                <span>
                  <ContactLink href={`tel:${siteSettings.contactInfo.phone}`}>
                    {siteSettings.contactInfo.phone}
                  </ContactLink><br />
                  <ContactLink href={`tel:${siteSettings.contactInfo.phoneSecondary}`}>
                    {siteSettings.contactInfo.phoneSecondary}
                  </ContactLink>
                </span>
              </ContactItem>
              <ContactItem>
                <FiMail />
                <span>
                  <ContactLink href={`mailto:${siteSettings.contactInfo.email}`}>
                    {siteSettings.contactInfo.email}
                  </ContactLink>
                </span>
              </ContactItem>
              <ContactItem>
                <FiMapPin />
                <span>{siteSettings.contactInfo.address || '200 Gilchrist Dr, Campbelltown NSW 2560'}</span>
              </ContactItem>
            </ContactInfo>
          </FooterSection>

          <FooterSection>
            <h3>Quick Links</h3>
            <QuickLinks>
              {quickLinks.map((link) => (
                <FooterLink key={link.path} to={link.path}>
                  {link.label}
                </FooterLink>
              ))}
            </QuickLinks>
          </FooterSection>

          <FooterSection>
            <h3>Follow Us On</h3>
            <SocialLinks>
              {socialLinks.map((social) => (
                <SocialIcon
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  onClick={(e) => {
                    if (social.url === 'javascript:void(0)') {
                      e.preventDefault();
                    }
                  }}
                >
                  <social.icon />
                </SocialIcon>
              ))}
            </SocialLinks>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <ClickableCopyright onClick={handleCopyrightClick}>
            &copy; {currentYear} RPL Support. All rights reserved.
          </ClickableCopyright>
          <PoweredBy>
            Powered by <a href="https://clickbit.com.au" target="_blank" rel="noopener noreferrer">ClickBit</a>
          </PoweredBy>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 